import React, { useEffect, useRef, useState } from "react"
import { Box, Divider, IconButton, TextField, Tooltip, Typography } from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import FormatBoldIcon from "@material-ui/icons/FormatBold"
import FormatItalicIcon from "@material-ui/icons/FormatItalic"
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined"
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted"
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered"
import LinkIcon from "@material-ui/icons/Link"
import ImageIcon from "@material-ui/icons/Image"
import FormatClearIcon from "@material-ui/icons/FormatClear"
import UndoIcon from "@material-ui/icons/Undo"
import RedoIcon from "@material-ui/icons/Redo"
import CodeIcon from "@material-ui/icons/Code"

const useStyles = makeStyles(theme => ({
    caixa: {
        border: "1px solid rgba(0,0,0,0.23)",
        borderRadius: 4,
        backgroundColor: "white",
        "&:hover": { borderColor: "rgba(0,0,0,0.87)" },
        "&$focada": { borderColor: theme.palette.primary.main, borderWidth: 2 },
    },
    focada: {},
    barra: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 2,
        padding: theme.spacing(0.5),
        backgroundColor: "#f8f9fa",
    },
    botao: {
        padding: 6,
    },
    editor: {
        padding: theme.spacing(1.5),
        outline: "none",
        overflowY: "auto",
        fontSize: "0.875rem",
        lineHeight: 1.5,
        wordBreak: "break-word",
        "& p": { margin: "0 0 0.5em" },
        "& ul, & ol": { paddingLeft: "1.5em", margin: "0 0 0.5em" },
        "& img": { maxWidth: "100%", height: "auto" },
        "&:empty:before": {
            content: "attr(data-placeholder)",
            color: "#aaa",
        },
    },
    rotulo: {
        color: "#7f8c8d",
        marginBottom: 4,
        display: "block",
    },
}))

type Props = {
    label?: string
    value: string
    onChange: (html: string) => void
    /** Altura mínima da área de edição, em px. */
    altura?: number
    placeholder?: string
    /**
     * Habilita o botão de imagem. Deve abrir o seletor de imagens de quem usa o editor
     * (galeria, upload, o que for) e resolver com a URL escolhida — ou null se desistir.
     * O editor cuida de guardar e restaurar o cursor enquanto o seletor está aberto.
     */
    selecionarImagem?: () => Promise<string | null>
}

/**
 * Editor de HTML simples (negrito, itálico, listas, link) com alternância para o
 * código-fonte. Usa document.execCommand: está marcado como obsoleto na especificação,
 * mas continua sendo o único caminho sem dependência externa que todos os navegadores
 * suportados pelo painel implementam.
 */
export default function EditorHtml({
    label,
    value,
    onChange,
    altura = 220,
    placeholder,
    selecionarImagem,
}: Props) {
    const classes = useStyles()
    const areaRef = useRef<HTMLDivElement | null>(null)
    const selecaoSalva = useRef<Range | null>(null)
    const [modoCodigo, setModoCodigo] = useState(false)
    const [focada, setFocada] = useState(false)

    // Só reescreve o HTML quando a mudança veio de fora (carregamento dos dados ou
    // edição pelo código-fonte). Reescrever a cada tecla jogaria o cursor para o início.
    useEffect(() => {
        const area = areaRef.current
        if (!area) return
        if (area.innerHTML !== value) area.innerHTML = value || ""
    }, [value, modoCodigo])

    const linhasCodigo = Math.max(6, Math.round(altura / 24))

    const emitir = () => onChange(areaRef.current?.innerHTML ?? "")

    const executar = (comando: string, argumento?: string) => {
        areaRef.current?.focus()
        document.execCommand(comando, false, argumento)
        emitir()
    }

    const inserirLink = () => {
        const url = window.prompt("Endereço do link (ex.: https://clientefiel.com.br)")
        if (!url) return
        executar("createLink", url)
    }

    // Abrir o seletor de imagens tira o foco do editor e, com ele, o cursor. Guarda a
    // posição antes e devolve depois, senão a imagem cairia sempre no fim do texto.
    const guardarSelecao = () => {
        const area = areaRef.current
        const selecao = window.getSelection()
        if (!area || !selecao || selecao.rangeCount === 0) return
        if (area.contains(selecao.getRangeAt(0).commonAncestorContainer)) {
            selecaoSalva.current = selecao.getRangeAt(0).cloneRange()
        }
    }

    const restaurarSelecao = () => {
        const area = areaRef.current
        if (!area) return
        area.focus()
        const selecao = window.getSelection()
        if (!selecao) return
        selecao.removeAllRanges()
        if (selecaoSalva.current) {
            selecao.addRange(selecaoSalva.current)
            return
        }
        // Nunca houve cursor no editor: insere no fim do conteúdo.
        const fim = document.createRange()
        fim.selectNodeContents(area)
        fim.collapse(false)
        selecao.addRange(fim)
    }

    const inserirImagem = async () => {
        if (!selecionarImagem) return
        guardarSelecao()
        const url = await selecionarImagem()
        if (!url) return
        restaurarSelecao()
        const src = url.replace(/"/g, "&quot;")
        document.execCommand("insertHTML", false, `<img src="${src}" />`)
        // O Chrome descarta o atributo style vindo no insertHTML, então ele é aplicado
        // depois: sem isso a imagem sai do editor sem limite e estoura a largura onde o
        // alerta é exibido.
        areaRef.current?.querySelectorAll("img:not([style])").forEach(img => {
            img.setAttribute("style", "max-width:100%")
        })
        emitir()
    }

    // Cola sempre como texto puro: evita trazer o markup de Word/páginas web para dentro
    // do regulamento, que é lido pelo app do cliente.
    const colarSemFormatacao = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const texto = e.clipboardData.getData("text/plain")
        document.execCommand("insertText", false, texto)
        emitir()
    }

    const ferramentas: Array<{ titulo: string; icone: React.ReactNode; acao: () => void }> = [
        { titulo: "Negrito", icone: <FormatBoldIcon fontSize="small" />, acao: () => executar("bold") },
        { titulo: "Itálico", icone: <FormatItalicIcon fontSize="small" />, acao: () => executar("italic") },
        {
            titulo: "Sublinhado",
            icone: <FormatUnderlinedIcon fontSize="small" />,
            acao: () => executar("underline"),
        },
        {
            titulo: "Lista com marcadores",
            icone: <FormatListBulletedIcon fontSize="small" />,
            acao: () => executar("insertUnorderedList"),
        },
        {
            titulo: "Lista numerada",
            icone: <FormatListNumberedIcon fontSize="small" />,
            acao: () => executar("insertOrderedList"),
        },
        { titulo: "Inserir link", icone: <LinkIcon fontSize="small" />, acao: inserirLink },
        ...(selecionarImagem
            ? [
                  {
                      titulo: "Inserir imagem da galeria",
                      icone: <ImageIcon fontSize="small" />,
                      acao: inserirImagem,
                  },
              ]
            : []),
        {
            titulo: "Limpar formatação",
            icone: <FormatClearIcon fontSize="small" />,
            acao: () => executar("removeFormat"),
        },
        { titulo: "Desfazer", icone: <UndoIcon fontSize="small" />, acao: () => executar("undo") },
        { titulo: "Refazer", icone: <RedoIcon fontSize="small" />, acao: () => executar("redo") },
    ]

    return (
        <Box>
            {label && (
                <Typography variant="caption" className={classes.rotulo}>
                    {label}
                </Typography>
            )}
            <Box className={`${classes.caixa} ${focada ? classes.focada : ""}`}>
                <Box className={classes.barra}>
                    {!modoCodigo &&
                        ferramentas.map(f => (
                            <Tooltip key={f.titulo} title={f.titulo}>
                                <IconButton
                                    size="small"
                                    className={classes.botao}
                                    // Impede que o clique tire a seleção de dentro do editor,
                                    // sem o que o execCommand não teria em que aplicar.
                                    onMouseDown={e => e.preventDefault()}
                                    onClick={f.acao}
                                >
                                    {f.icone}
                                </IconButton>
                            </Tooltip>
                        ))}
                    <Box flexGrow={1} />
                    <Tooltip title={modoCodigo ? "Voltar ao editor" : "Editar código HTML"}>
                        <IconButton
                            size="small"
                            className={classes.botao}
                            color={modoCodigo ? "primary" : "default"}
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => setModoCodigo(m => !m)}
                        >
                            <CodeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Divider />
                {modoCodigo ? (
                    <TextField
                        multiline
                        fullWidth
                        // minRows = maxRows deixa a caixa com a mesma altura do editor
                        // visual e rola por dentro, em vez de crescer com o regulamento.
                        minRows={linhasCodigo}
                        maxRows={linhasCodigo}
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        InputProps={{
                            disableUnderline: true,
                            style: { fontFamily: "monospace", fontSize: "0.8rem", padding: 12 },
                        }}
                    />
                ) : (
                    <div
                        ref={areaRef}
                        className={classes.editor}
                        // Altura fixa com rolagem própria: sem o teto, uma imagem alta
                        // empurraria o resto do formulário para fora da tela.
                        style={{ height: altura }}
                        contentEditable
                        suppressContentEditableWarning
                        data-placeholder={placeholder}
                        onInput={emitir}
                        onBlur={() => {
                            setFocada(false)
                            emitir()
                        }}
                        onFocus={() => setFocada(true)}
                        onPaste={colarSemFormatacao}
                    />
                )}
            </Box>
        </Box>
    )
}
